// src/components/dashboard/tabs/DashboardCoursesTab.js
import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Button,
    CircularProgress,
    Box,
    Paper,
    Divider,
    Tab,
    Tabs,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import {
    Book,
    Plus,
    CheckCircle2,
    Clock,
    FileText,
    Rocket,
    ClipboardCheck,
    Link as LinkIcon,
} from 'lucide-react';
import GradientButton from '../../ui/GradientButton';
import { AuthContext } from '../../../App';
import { useCoursesStore } from '../../../stores';
import {
    fetchUserAssignments,
    fetchCreatedAssignments,
    deleteCourse,
    deleteAssignment
} from '../../../services/api';
import { useDataFetching } from '../../../hooks/useDataFetching';
import { usePagination } from '../../../hooks/usePagination';
import { useError } from '../../../contexts/ErrorContext';
import CreateAssignmentDialog from '../../assignments/CreateAssignmentDialog';
import GenerateLinkDialog from '../../assignments/GenerateLinkDialog';
import CourseAccessDialog from './CourseAccessDialog';
import AttachAITutorDialog from './AttachAITutorDialog';
import AssignmentDetailDialog from '../../assignments/AssignmentDetailDialog';
import CourseCard from './CourseCard';
import AssignTaskDialog from './AssignTaskDialog';
import SandboxAssignment from '../../sandbox/SandboxAssignment';

const DashboardCoursesTab = ({ token, profile }) => {
    const { t } = useTranslation();
    const { userInfo } = useContext(AuthContext);
    const navigate = useNavigate();
    const { showError } = useError();

    // Use Zustand stores
    const {
        enrolledCourses,
        createdCourses,
        loading,
        fetchCreatedCourses: fetchCreatedCoursesFromStore,
    } = useCoursesStore();

    const {
        data: assignedTasks,
        isLoading: loadingAssignedTasks,
        refreshData: refreshAssignedTasks
    } = useDataFetching(
        async () => {
            const response = await fetchUserAssignments(token, {
                onError: (error) => showError(error),
                defaultValue: []
            });
            return Array.isArray(response) ? response : [];
        },
        [token]
    );

    const loadingEnrollments = loading;
    const loadingCreatedCourses = loading;

    // Состояния для тьютора
    const [isTutor, setIsTutor] = useState(false);
    const [tutorTabValue, setTutorTabValue] = useState(0); // По умолчанию открываем вкладку с заданиями

    // Определяем, является ли пользователь тьютором
    useEffect(() => {
        if (profile) {
            const isTutorValue = profile.is_tutor || userInfo.profile?.is_tutor || userInfo.is_tutor || false;
            setIsTutor(isTutorValue);
        }
    }, [profile, userInfo]);

    const refreshCreatedCourses = () => fetchCreatedCoursesFromStore(token);

    const {
        data: createdAssignments,
        isLoading: loadingCreatedAssignments,
        refreshData: refreshCreatedAssignments,
        forceFetch: forceRefreshCreatedAssignments
    } = useDataFetching(
        async () => {
            if (!isTutor || !token) return [];
            const response = await fetchCreatedAssignments(token, {
                onError: (error) => showError(error),
                defaultValue: []
            });
            return Array.isArray(response) ? response : [];
        },
        [token, isTutor]
    );

    // Пагинация для созданных заданий
    const {
        currentItems: paginatedCreatedAssignments,
        currentPage: assignmentsPage,
        totalPages: assignmentsTotalPages,
        nextPage: nextAssignmentsPage,
        prevPage: prevAssignmentsPage
    } = usePagination(createdAssignments || [], 10);

    // Загружаем данные для тьюторов
    useEffect(() => {
        if (isTutor && token) {
            fetchCreatedCoursesFromStore(token).catch(err => showError(err));
            // Принудительно загружаем созданные задания
            forceRefreshCreatedAssignments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTutor, token]);

    // Состояния для выбранных элементов
    const [deletingItem, setDeletingItem] = useState(null);
    const [deletingItemType, setDeletingItemType] = useState('');
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [assignmentDialogMode, setAssignmentDialogMode] = useState('student'); // 'student' or 'tutor'

    // Состояние для удаления
    const [isDeleting, setIsDeleting] = useState(false);

    // Состояния для диалогов
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [accessDialogOpen, setAccessDialogOpen] = useState(false);
    const [attachTutorDialogOpen, setAttachTutorDialogOpen] = useState(false);
    const [assignmentDetailsOpen, setAssignmentDetailsOpen] = useState(false);
    const [createAssignmentDialogOpen, setCreateAssignmentDialogOpen] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState(null);
    const [generateLinkDialogOpen, setGenerateLinkDialogOpen] = useState(false);
    const [linkAssignment, setLinkAssignment] = useState(null);
    const [assignTaskDialogOpen, setAssignTaskDialogOpen] = useState(false);
    const [setError] = useState(null);


    // Обработчик изменения вкладки для тьюторов
    const handleTutorTabChange = (event, newValue) => {
        setTutorTabValue(newValue);
    };

    // Обработчик открытия диалога удаления
    const handleOpenDeleteDialog = (item, type) => {
        setDeletingItem(item);
        setDeletingItemType(type);
        setDeleteDialogOpen(true);
    };

    // Обработчик удаления элемента
    const handleDelete = async () => {
        if (!deletingItem || !deletingItemType) return;

        setIsDeleting(true);

        try {
            if (deletingItemType === 'course') {
                await deleteCourse(token, deletingItem.id, {
                    onError: (error) => showError(error)
                });
                refreshCreatedCourses();
            } else if (deletingItemType === 'assignment') {
                await deleteAssignment(token, deletingItem.id, {
                    onError: (error) => showError(error)
                });
                refreshCreatedAssignments();
            }
        } catch (err) {

            showError(t(`dashboard.courses.error.delete_${deletingItemType}`));
        } finally {
            setIsDeleting(false);
            setDeleteDialogOpen(false);
            setDeletingItem(null);
            setDeletingItemType('');
        }
    };

    // Обработчик успешного создания задания
    const handleAssignmentCreated = () => {
        // Закрываем диалог создания
        setCreateAssignmentDialogOpen(false);

        // Перезагружаем список заданий
        refreshAssignedTasks();
        if (isTutor) {
            refreshCreatedAssignments();
            // Переключаемся на вкладку заданий (индекс 0)
            setTutorTabValue(0);
        }
    };

    // Обработчик открытия диалога управления доступом
    const handleOpenAccessDialog = (course) => {
        setSelectedCourse(course);
        setAccessDialogOpen(true);
    };

    // Обработчик открытия диалога прикрепления тьютора
    const handleOpenAttachTutorDialog = (course) => {
        setSelectedCourse(course);
        setAttachTutorDialogOpen(true);
    };

    // Обработчик открытия деталей задания
    const handleOpenAssignmentDetails = (assignment, mode = 'student') => {
        setSelectedAssignment(assignment);
        setAssignmentDialogMode(mode);
        setAssignmentDetailsOpen(true);
    };

    // Обработчик открытия диалога назначения задания
    const handleAssignTask = (assignment) => {
        setSelectedAssignment(assignment);
        setAssignTaskDialogOpen(true);
    };

    // Рендер секции курсов пользователя
    const renderEnrollments = () => (
        <section>
            <Box className="flex items-center justify-between mb-6">
                <Box className="flex items-center gap-2">
                    <Book className="w-6 h-6 text-green-500" />
                    <Typography variant="h6" className="font-semibold">
                        {t('dashboard.my_courses')}
                    </Typography>
                </Box>
                <Button
                    onClick={() => navigate('/courses')}
                    variant="outlined"
                    startIcon={<Plus className="w-4 h-4" />}
                >
                    {t('dashboard.browse_courses')}
                </Button>
            </Box>

            {loadingEnrollments ? (
                <Box className="flex justify-center py-8">
                    <CircularProgress />
                </Box>
            ) : enrolledCourses.length > 0 ? (
                <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {enrolledCourses.map(enrollment => (
                        <Paper
                            key={enrollment.id}
                            className="p-4 rounded-lg border border-gray-100 hover:border-gray-200 transition-all"
                            sx={{
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)', boxShadow: 2 }
                            }}
                        >
                            <Box className="flex justify-between items-start">
                                <Box>
                                    <Typography
                                        variant="h6"
                                        className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                                        onClick={() => navigate(`/courses/${enrollment.course.id}`)}
                                    >
                                        {enrollment.course.title}
                                    </Typography>
                                    {enrollment.course.description && (
                                        <Typography variant="body2" className="text-gray-600 mt-1">
                                            {enrollment.course.description.length > 100
                                                ? `${enrollment.course.description.substring(0, 100)}...`
                                                : enrollment.course.description}
                                        </Typography>
                                    )}
                                </Box>
                                <Box className="flex items-center gap-2">
                                    {enrollment.progress >= 100 ? (
                                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                                    ) : (
                                        <Clock className="w-5 h-5 text-blue-500" />
                                    )}
                                </Box>
                            </Box>
                            <Box className="mt-4">
                                <Box className="w-full bg-gray-200 rounded-full h-2">
                                    <Box
                                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${enrollment.progress || 0}%` }}
                                    />
                                </Box>
                                <Typography variant="body2" className="text-gray-600 mt-1">
                                    {t('dashboard.progress')}: {Math.round(enrollment.progress || 0)}%
                                </Typography>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            ) : (
                <Alert severity="info" className="my-6">
                    {t('dashboard.no_active_courses')}
                </Alert>
            )}
        </section>
    );

    // Рендер секции заданий назначенных пользователю
    const renderAssignedTasks = () => (
        <section>
            <Box className="flex items-center justify-between mb-6">
                <Box className="flex items-center gap-2">
                    <ClipboardCheck className="w-6 h-6 text-purple-500" />
                    <Typography variant="h6" className="font-semibold">
                        {t('dashboard.courses.assigned_tasks')}
                    </Typography>
                </Box>
            </Box>

            {loadingAssignedTasks ? (
                <Box className="flex justify-center py-8">
                    <CircularProgress />
                </Box>
            ) : (assignedTasks || []).length > 0 ? (
                <Box className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('dashboard.courses.table.title')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('dashboard.courses.table.course')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('dashboard.courses.table.due_date')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('dashboard.courses.table.status')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('dashboard.courses.table.actions')}
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {(assignedTasks || []).map(task => (
                            <React.Fragment key={task.id}>
                            <tr
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={(e) => {
                                    // Don't trigger if clicking on buttons
                                    if (!e.target.closest('button')) {
                                        if (task.assignment_details.lesson) {
                                            navigate(`/courses/${task.assignment_details.lesson.module.course.id}/lessons/${task.assignment_details.lesson.id}`);
                                        } else {
                                            handleOpenAssignmentDetails(task.assignment_details, 'student');
                                        }
                                    }
                                }}
                            >
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Box className="flex items-center">
                                        {task.assignment_details.assignment_type === 'sandbox' && (
                                            <Rocket className="w-5 h-5 text-orange-500 mr-2" />
                                        )}
                                        {task.assignment_details.assignment_type === 'text' && (
                                            <FileText className="w-5 h-5 text-blue-500 mr-2" />
                                        )}
                                        {(task.assignment_details.assignment_type === 'test' ||
                                            task.assignment_details.assignment_type === 'quiz') && (
                                            <ClipboardCheck className="w-5 h-5 text-purple-500 mr-2" />
                                        )}
                                        <Typography className="text-sm font-medium text-gray-900">
                                            {task.assignment_details.title}
                                        </Typography>
                                    </Box>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Typography className="text-sm text-gray-500">
                                        {task.assignment_details.lesson?.module?.course?.title || t('dashboard.courses.standalone_assignment')}
                                    </Typography>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Typography className="text-sm text-gray-500">
                                        {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                                    </Typography>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Box className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        task.status === 'graded'
                                            ? 'bg-green-100 text-green-800'
                                            : task.status === 'submitted'
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {task.status === 'graded'
                                            ? t('dashboard.courses.status.graded')
                                            : task.status === 'submitted'
                                            ? t('dashboard.courses.status.submitted')
                                            : t('dashboard.courses.status.assigned')}
                                        {task.submission_info?.has_feedback && (
                                            <FileText className="w-3 h-3 ml-1" title={t('dashboard.courses.has_feedback')} />
                                        )}
                                    </Box>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {task.assignment_details.assignment_type === 'sandbox' ? (
                                        <SandboxAssignment
                                            assignment={task.assignment_details}
                                            hideContainer={true}
                                            compact={true}
                                        />
                                    ) : (
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => task.assignment_details.lesson
                                                ? navigate(`/courses/${task.assignment_details.lesson.module.course.id}/lessons/${task.assignment_details.lesson.id}`)
                                                : handleOpenAssignmentDetails(task.assignment_details, 'student')}
                                        >
                                            {t('dashboard.courses.go_to_task')}
                                        </Button>
                                    )}
                                </td>
                            </tr>
                            </React.Fragment>
                        ))}
                        </tbody>
                    </table>
                </Box>
            ) : (
                <Alert severity="info" className="my-6">
                    {t('dashboard.courses.no_assigned_tasks')}
                </Alert>
            )}
        </section>
    );

    // Рендер секции курсов для тьюторов
    const renderTutorCourses = () => (
        <section>
            <Box className="flex items-center justify-between mb-6">
                <Box className="flex items-center gap-2">
                    <Book className="w-6 h-6 text-indigo-500" />
                    <Typography variant="h6" className="font-semibold">
                        {t('dashboard.courses.created_courses')}
                    </Typography>
                </Box>
                <GradientButton
                    onClick={() => navigate('/course-constructor')}
                    startIcon={<Plus className="w-4 h-4" />}
                >
                    {t('dashboard.courses.create_course')}
                </GradientButton>
            </Box>

            {loadingCreatedCourses ? (
                <Box className="flex justify-center py-8">
                    <CircularProgress />
                </Box>
            ) : createdCourses.length > 0 ? (
                <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {createdCourses.map(course => (
                        <CourseCard
                            key={course.id}
                            course={course}
                            onManageAccess={handleOpenAccessDialog}
                            onAttachTutor={handleOpenAttachTutorDialog}
                            onDelete={handleOpenDeleteDialog}
                        />
                    ))}
                </Box>
            ) : (
                <Alert severity="info" className="my-6">
                    {t('dashboard.courses.no_created_courses')}
                </Alert>
            )}
        </section>
    );

    // Рендер секции заданий для тьюторов
    const renderTutorAssignments = () => (
        <section>
            <Box className="flex items-center justify-between mb-6">
                <Box className="flex items-center gap-2">
                    <ClipboardCheck className="w-6 h-6 text-indigo-500" />
                    <Typography variant="h6" className="font-semibold">
                        {t('dashboard.courses.created_assignments')}
                    </Typography>
                </Box>
                <GradientButton
                    onClick={() => setCreateAssignmentDialogOpen(true)}
                    startIcon={<Plus className="w-4 h-4" />}
                >
                    {t('dashboard.courses.create_assignment')}
                </GradientButton>
            </Box>

            {loadingCreatedAssignments ? (
                <Box className="flex justify-center py-8">
                    <CircularProgress />
                </Box>
            ) : createdAssignments.length > 0 ? (
                <>
                <Box className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('dashboard.courses.table.title')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('dashboard.courses.table.course')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('dashboard.courses.table.submissions')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('dashboard.courses.table.type')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('dashboard.courses.table.actions')}
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedCreatedAssignments.map(assignment => (
                            <tr
                                key={assignment.id}
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={(e) => {
                                    // Don't trigger if clicking on buttons
                                    if (!e.target.closest('button')) {
                                        handleOpenAssignmentDetails(assignment, 'tutor');
                                    }
                                }}
                            >
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Box className="flex items-center">
                                        {assignment.assignment_type === 'sandbox' && (
                                            <Rocket className="w-5 h-5 text-orange-500 mr-2" />
                                        )}
                                        {assignment.assignment_type === 'text' && (
                                            <FileText className="w-5 h-5 text-blue-500 mr-2" />
                                        )}
                                        {(assignment.assignment_type === 'test' ||
                                            assignment.assignment_type === 'quiz') && (
                                            <ClipboardCheck className="w-5 h-5 text-purple-500 mr-2" />
                                        )}
                                        <Typography className="text-sm font-medium text-gray-900">
                                            {assignment.title}
                                        </Typography>
                                    </Box>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Typography className="text-sm text-gray-500">
                                        {assignment.lesson?.module?.course?.title ||
                                            (assignment.is_standalone ? t('dashboard.courses.standalone_assignment') : '-')}
                                    </Typography>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Typography className="text-sm text-gray-500">
                                        {assignment.submissions_count || 0} {t('dashboard.courses.table.submissions_count')}
                                    </Typography>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Box className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        assignment.is_standalone
                                            ? 'bg-purple-100 text-purple-800'
                                            : 'bg-blue-100 text-blue-800'
                                    }`}>
                                        {assignment.is_standalone
                                            ? t('dashboard.courses.type.standalone')
                                            : t('dashboard.courses.type.course')}
                                    </Box>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Box className="flex gap-2">
                                        <Button
                                            variant="text"
                                            color="info"
                                            size="small"
                                            onClick={() => {
                                                setEditingAssignment(assignment);
                                                setCreateAssignmentDialogOpen(true);
                                            }}
                                        >
                                            {t('dashboard.courses.table.edit')}
                                        </Button>
                                        <Button
                                            variant="text"
                                            color="success"
                                            size="small"
                                            startIcon={<LinkIcon className="w-4 h-4" />}
                                            onClick={() => {
                                                setLinkAssignment(assignment);
                                                setGenerateLinkDialogOpen(true);
                                            }}
                                        >
                                            {t('dashboard.courses.table.link')}
                                        </Button>
                                        <Button
                                            variant="text"
                                            color="primary"
                                            size="small"
                                            onClick={() => handleAssignTask(assignment)}
                                        >
                                            {t('dashboard.courses.table.assign')}
                                        </Button>
                                        <Button
                                            variant="text"
                                            color="error"
                                            size="small"
                                            onClick={() => handleOpenDeleteDialog(assignment, 'assignment')}
                                        >
                                            {t('dashboard.courses.table.delete')}
                                        </Button>
                                    </Box>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </Box>

                {/* Пагинация */}
                {assignmentsTotalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
                        <Button
                            size="small"
                            onClick={prevAssignmentsPage}
                            disabled={assignmentsPage === 1}
                        >
                            {t('common.previous')}
                        </Button>
                        <Typography variant="body2">
                            {t('common.page')} {assignmentsPage} {t('common.of')} {assignmentsTotalPages}
                        </Typography>
                        <Button
                            size="small"
                            onClick={nextAssignmentsPage}
                            disabled={assignmentsPage === assignmentsTotalPages}
                        >
                            {t('common.next')}
                        </Button>
                    </Box>
                )}
                </>
            ) : (
                <Alert severity="info" className="my-6">
                    {t('dashboard.courses.no_created_assignments')}
                </Alert>
            )}
        </section>
    );

    return (
        <div className="space-y-8">
            {/* Секции для обычных пользователей */}
            {renderEnrollments()}
            {renderAssignedTasks()}

            {/* Секции для тьюторов */}
            {isTutor && (
                <>
                    <Divider className="my-8" />

                    <Box className="mb-6">
                        <Tabs
                            value={tutorTabValue}
                            onChange={handleTutorTabChange}
                            variant="fullWidth"
                            sx={{
                                borderBottom: 1,
                                borderColor: 'divider',
                                '& .MuiTab-root': { textTransform: 'none' }
                            }}
                        >
                            <Tab
                                label={t('dashboard.courses.tutor_tabs.assignments')}
                                icon={<ClipboardCheck className="w-4 h-4" />}
                                iconPosition="start"
                            />
                            <Tab
                                label={t('dashboard.courses.tutor_tabs.courses')}
                                icon={<Book className="w-4 h-4" />}
                                iconPosition="start"
                            />
                        </Tabs>
                    </Box>

                    {tutorTabValue === 0 ? renderTutorAssignments() : renderTutorCourses()}
                </>
            )}

            {/* Диалоги */}
            <CreateAssignmentDialog
                open={createAssignmentDialogOpen}
                onClose={() => {
                    setCreateAssignmentDialogOpen(false);
                    setEditingAssignment(null);
                }}
                onSuccess={handleAssignmentCreated}
                token={token}
                editMode={!!editingAssignment}
                assignmentData={editingAssignment}
            />

            <GenerateLinkDialog
                open={generateLinkDialogOpen}
                onClose={() => {
                    setGenerateLinkDialogOpen(false);
                    setLinkAssignment(null);
                }}
                assignment={linkAssignment}
                token={token}
            />

            <Dialog
                open={deleteDialogOpen}
                onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            >
                <DialogTitle color="error">
                    {t(`dashboard.courses.delete_${deletingItemType}_title`)}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t(`dashboard.courses.delete_${deletingItemType}_confirmation`, {
                            name: deletingItem?.title || ''
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={isDeleting}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={isDeleting}
                    >
                        {isDeleting ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {t('common.deleting')}
                                <CircularProgress size={16} sx={{ marginLeft: 1 }} />
                            </Box>
                        ) : (
                            t('common.delete')
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {selectedCourse && (
                <>
                    <CourseAccessDialog
                        open={accessDialogOpen}
                        onClose={() => setAccessDialogOpen(false)}
                        course={selectedCourse}
                        token={token}
                        setError={setError}
                    />

                    <AttachAITutorDialog
                        open={attachTutorDialogOpen}
                        onClose={() => setAttachTutorDialogOpen(false)}
                        course={selectedCourse}
                        token={token}
                        setError={setError}
                    />
                </>
            )}

            {selectedAssignment && (
                <AssignmentDetailDialog
                    open={assignmentDetailsOpen}
                    onClose={() => {
                        setAssignmentDetailsOpen(false);
                        setSelectedAssignment(null);
                    }}
                    assignment={selectedAssignment}
                    token={token}
                    mode={assignmentDialogMode}
                />
            )}

            {selectedAssignment && (
                <AssignTaskDialog
                    open={assignTaskDialogOpen}
                    onClose={() => setAssignTaskDialogOpen(false)}
                    assignment={selectedAssignment}
                    token={token}
                />
            )}
        </div>
    );
};

export default DashboardCoursesTab;
